import {
  AcceptIcon,
  BookmarkIcon,
  Box,
  Button,
  CallVideoIcon,
  ChevronEndIcon,
  ChevronStartIcon,
  CloseIcon,
  ContactGroupCallIcon,
  Divider,
  FilesPdfColoredIcon,
  FilesPdfIcon,
  FlagIcon,
  Flex,
  ItalicIcon,
  Loader,
  PollIcon,
  PresenterIcon,
  RetryIcon,
  ShiftActivityIcon,
  SurveyIcon,
  SyncIcon,
  Text,
  Tooltip,
  VolumeIcon,
} from "@fluentui/react-northstar";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useChartContext } from "../../../../../context/ChartContext";
import axios from "axios";
import { toast } from "react-toastify";
import { Card } from "antd";
import { HourglassOutlined } from "@ant-design/icons";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import ProgressChart from "../ProgressChart/ProgressChart";
import DateRange from "../DateRange/DateRange";
import "./TeacherReportCard.scss";
import DataBox from "./components/DataBox";
import LabelBox from "./components/LabelBox";
import WeeklyClassHours from "./components/WeeklyClassHours";

const chartTypes = {
  attendance: "attendance",
  assignment: "assignment",
  event: "event",
  feedback: "feedback",
  booking: "booking",
};

const TeacherReportCard = (props) => {
  let d = new Date();

  const { selectedTeacher } = props;
  const { t } = useTranslation();

  const translation = t("teacherReportCard");
  const attendanceTranslation = translation.statistics.attendance;
  const assignmentTranslation = translation.statistics.assignment;
  const eventTranslation = translation.statistics.event;
  const feedbackTranslation = translation.statistics.feedback;
  const bookingTranslation = translation.statistics.booking;

  const [loadedInfo, setLoadedInfo] = React.useState(null);
  const [teacherReportData, setTeacherReportData] = React.useState([]);
  const [customFieldInfo, setCustomFieldInfo] = React.useState(null);
  const [selectedChart, setSelectedChart] = React.useState(
    chartTypes.attendance
  );
  const [loader, setLoader] = React.useState(false);
  const [dateRange, setDateRange] = React.useState({
    startDate: new Date(
      d.getFullYear(),
      d.getMonth() - 3,
      d.getDate(),
      0,
      0,
      0,
      0
    ),
    endDate: new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 0, 0),
  });

  const teacherCardRef = useRef(null);

  const downloadPDF = async () => {
    if (!teacherCardRef.current) return;

    const input = teacherCardRef.current;

    // Ensure all images are loaded before capturing
    const imgPromises = Array.from(input.querySelectorAll("img")).map((img) => {
      return new Promise((resolve) => {
        if (img.complete) resolve();
        else {
          img.onload = resolve;
          img.onerror = resolve; // Prevent blocking if an image fails
        }
      });
    });

    await Promise.all(imgPromises);

    try {
      const canvas = await html2canvas(input, { useCORS: true }); // Higher quality
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 190; // Adjust to fit A4
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 10, 10, imgWidth, imgHeight);
      pdf.save("teacher-card.pdf");
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const getInfo = (email, dateRange) => {
    setLoader(true);
    setLoadedInfo(null);
    setCustomFieldInfo(null);
    setSelectedChart(chartTypes.attendance);
    const payload = {
      dateRange,
    };
    axios
      .post(
        `${process.env.REACT_APP_EP_URL}/api/admin/get-teacher-info/${email}${props.user.slug}`,
        payload,
        {
          headers: {
            Authorization: "Bearer " + props.user.accessToken,
          },
        }
      )
      .then((res) => {
        const response = res.data.result;
        const { assignment, feedback, event, info, chart, booking } = res.data;

        setLoadedInfo({
          ...response,
          Ausente: response?.Ausente ?? 0,
          Justificado:
            Number(response?.Justificado ?? 0) +
              Number(response?.Attendance_not_taken ?? 0),
          Presente: response?.Presente ?? 0,
          Retardo: response?.Retardo ?? 0,
          TeacherEmailId: response?.TeacherData.AttendanceTakenBy ?? "",
          StudentName: response?.TeacherData?.ATBName ?? "",
          Total: response?.Total ?? 0,

          assignment: {
            total: assignment?.total ?? 0,
            reSubmit: assignment?.Resubmit ?? 0,
            completed: assignment?.Completed ?? 0,
            pending: assignment?.Pending ?? 0,
            gradesPending: assignment?.["Grades Pending"] ?? 0,
          },
          event: {
            registered: event?.registered ?? 0,
            bookmarked: event?.bookmarked ?? 0,
            organized: event?.organized ?? 0,
            total: event?.total ?? 0,
          },
          feedback: {
            total: feedback?.total ?? 0,
            poll: feedback?.poll ?? 0,
            text: feedback?.text ?? 0,
            audio: feedback?.audio ?? 0,
            video: feedback?.video ?? 0,
            mixed: feedback?.mixed ?? 0,
            any: feedback?.any ?? 0,
          },
          info: {
            ...info,
            attendanceAverage: info.attendanceAverage ?? 0,
            assignmentAverage: info.assignmentAverage ?? 0,
          },
          chart,
          booking,
        });

        const teacherReportData = [
          {
            header: attendanceTranslation.header,
            chartType: chartTypes.attendance,
            info: {
              [attendanceTranslation.info.total]: response?.Total ?? 0,
              [attendanceTranslation.info.present]: response?.Presente ?? 0,
              [attendanceTranslation.info.absent]: response?.Retardo ?? 0,
              [attendanceTranslation.info.delayed]: response?.Ausente ?? 0,
              [attendanceTranslation.info.justified]:
                Number(response?.Justificado ?? 0) +
                Number(response?.Attendance_not_taken ?? 0),
            },
          },
          {
            header: assignmentTranslation.header,
            chartType: chartTypes.assignment,
            info: {
              [assignmentTranslation.info.total]: assignment?.total ?? 0,
              [assignmentTranslation.info.completed]:
                assignment?.Completed ?? 0,
              [assignmentTranslation.info.resubmit]: assignment?.Resubmit ?? 0,
              [assignmentTranslation.info.pending]: assignment?.Pending ?? 0,
              [assignmentTranslation.info.gradesPending]:
                assignment?.["Grades Pending"] ?? 0,
            },
          },
          {
            header: eventTranslation.header,
            chartType: chartTypes.event,
            info: {
              [eventTranslation.info.total]: event?.total ?? 0,
              [eventTranslation.info.registered]: event?.registered ?? 0,
              [eventTranslation.info.favorites]: event?.bookmarked ?? 0,
              [eventTranslation.info.organized]: event?.organized ?? 0,
            },
          },
          {
            header: feedbackTranslation.header,
            chartType: chartTypes.feedback,
            info: {
              [feedbackTranslation.info.total]: feedback?.total ?? 0,
              [feedbackTranslation.info.poll]: feedback?.poll ?? 0,
              [feedbackTranslation.info.text]: feedback?.text ?? 0,
              [feedbackTranslation.info.audio]: feedback?.audio ?? 0,
              [feedbackTranslation.info.video]: feedback?.video ?? 0,
            },
          },
          {
            header: bookingTranslation.header,
            chartType: chartTypes.booking,
            info: {
              [bookingTranslation.info.total]: booking?.total || 0,
              [bookingTranslation.info.availableSlots]:
                booking?.availableSlots || 0,
              [bookingTranslation.info.effectiveness]:
                parseInt(booking?.effectiveness) > 0
                  ? `${parseInt(booking?.effectiveness)} %`
                  : 0,
              [bookingTranslation.info.favorites]: booking?.favorites || 0,
              [bookingTranslation.info.reviews]: booking?.reviews || 0,
            },
          },
        ];
        setTeacherReportData(teacherReportData);
        setCustomFieldInfo(res.data.custom_field);
        setLoader(false);
      })
      .catch((err) => {
        setLoader(false);
        toast.error(err?.response?.data?.message);
        console.log(err);

        if (err.response?.status === 401) {
          setAccDlg(true);
        }
      });
  };

  const changeRoleHandler = (email) => {
    return (
      <Button
        style={{ width: "fit-content" }}
        icon={<SyncIcon />}
        content={t("setting").teacher[6]}
        iconPosition="before"
        disabled={
          props.user.slug === "?slug=acvpreprod" ||
          props.user.slug === "?slug=acvapp"
        }
        onClick={() => {
          axios
            .post(
              `${process.env.REACT_APP_EP_URL}/api/admin/change-teacher-role-to-admin${props.user.slug}`,
              {
                email: email,
              },
              {
                headers: {
                  Authorization: "Bearer " + props.user.accessToken,
                },
              }
            )
            .then((res) => {
              getInfo(email, dateRange);
            })
            .catch((err) => {
              toast.error(err?.response?.data?.message);
              console.log(err);
            });
        }}
      />
    );
  };

  const chartSelectHandler = (type) => {
    setSelectedChart(type);
  };

  const scrollButtonHandler = (isScrollToRight) => {
    document.getElementById("scroll-statictics-container").scrollBy({
      left: isScrollToRight ? 150 : -150,
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
    console.log("newDateRange", newDateRange);
    getInfo(selectedTeacher.key, newDateRange);
  };

  useEffect(() => {
    if (selectedTeacher) {
      getInfo(selectedTeacher.key, dateRange);
    }
  }, [selectedTeacher]);

  return (
    <Card ref={teacherCardRef}>
      <Flex
        gap="gap.large"
        className="teacher-data-wrapper"
        wrap={props.isMobileView}
      >
        <Flex column className="teacher-image-info-wrapper" gap="gap.medium">
          <div className="teacher-image-wrapper">
            <img
              src={selectedTeacher?.image}
              className="teacher-image"
              crossOrigin="anonymous"
            />
          </div>
          {loadedInfo ? (
            <Flex
              column
              className="teacher-info-wrapper"
              fill
              gap="gap.smaller"
            >
              <Flex gap="gap.small" column>
                <LabelBox label={`${translation.common.name}:`} />

                <Text
                  content={
                    loadedInfo?.info?.teacherAccountInfo?.TeacherName || ""
                  }
                  truncated
                  title={loadedInfo?.info?.teacherAccountInfo?.TeacherName}
                  style={{ padding: "0 5px" }}
                />
              </Flex>
              <Flex gap="gap.small" column>
                <LabelBox label={`${translation.common.account}:`} />

                <Text
                  content={loadedInfo?.AttendanceTakenBy}
                  truncated
                  title={loadedInfo?.AttendanceTakenBy}
                  style={{ padding: "0 5px" }}
                />
              </Flex>

              <Flex gap="gap.small" column>
                <LabelBox
                  label={`${translation.common.attendanceEffectiveness}:`}
                />

                <Text
                  content={
                    Number(loadedInfo?.info?.attendanceEffectiveness) > 0
                      ? loadedInfo?.info?.attendanceEffectiveness + " %"
                      : 0
                  }
                  style={{ padding: "0 5px" }}
                />
              </Flex>
              <Flex gap="gap.small" column>
                <LabelBox
                  label={`${translation.common.assignmentsEffectiveness}:`}
                />

                <Text
                  content={
                    Number(loadedInfo?.info?.assignmentEffectiveness) > 0
                      ? loadedInfo?.info?.assignmentEffectiveness + " %"
                      : 0
                  }
                  style={{ padding: "0 5px" }}
                />
              </Flex>
              <Flex gap="gap.small" column>
                <LabelBox label={`${translation.common.bookingSessions}:`} />

                <Text
                  content={Number(loadedInfo?.booking?.meetings)}
                  style={{ padding: "0 5px" }}
                />
              </Flex>
              <Flex gap="gap.small" column>
                <LabelBox label={`${translation.common.weeklyClassHours}:`} />
                <Text
                  content={
                    loadedInfo?.info?.weeklyClassSummary?.weeklyClassHours || 0
                  }
                  style={{ padding: "0 5px" }}
                />
              </Flex>

              <Flex hAlign="center" style={{ marginTop: "20px" }}>
                {props.user.actualRole === "SuperAdmin"
                  ? changeRoleHandler(selectedTeacher.key)
                  : null}
              </Flex>
            </Flex>
          ) : (
            ""
          )}
        </Flex>
        <Flex column className="info-chart-wrapper">
          <Flex hAlign="end">
            <DateRange
              dateRange={dateRange}
              handleDateRangeChange={handleDateRangeChange}
            />
          </Flex>
          {loadedInfo && teacherReportData.length ? (
            <Flex column gap="gap.large">
              <Flex
                gap="gap.large"
                fill
                className="teacher-statictics-wrapper"
                space="between"
                style={{overflowX:"auto"}}
              >
                {teacherReportData.map((data, i) => {
                  return (
                    <DataBox
                      key={`teacherReportData-${i}`}
                      header={data.header}
                      info={data.info}
                      changeChartType={() => chartSelectHandler(data.chartType)}
                    />
                  );
                })}
              </Flex>
              <Flex gap="gap.large">
                <ProgressChart
                  selectedChart={selectedChart}
                  data={loadedInfo.chart}
                  chartTypes={chartTypes}
                />
                <Flex column gap="gap.large" hAlign="end" space="around">
                  <WeeklyClassHours
                    classHours={
                      loadedInfo?.info?.weeklyClassSummary?.classHours || {}
                    }
                    weeklyClassHours={
                      loadedInfo?.info?.weeklyClassSummary?.weeklyClassHours ||
                      0
                    }
                  />
                  <Flex
                    style={{ width: "fit-content" }}
                    column
                    gap="gap.medium"
                  >
                    <div
                      style={{
                        backgroundColor: "#f2f2f2",
                        padding: "2px 30px",
                      }}
                    >
                      <Text
                        content={translation.common.downloadCard}
                        weight="semibold"
                        align="center"
                      />
                    </div>
                    <Button
                      icon={<FilesPdfIcon size="largest" />}
                      iconOnly
                      text
                      onClick={downloadPDF}
                    />
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          ) : (
            loader && (
              <Flex fill hAlign="center" vAlign="center" column>
                <Loader size="small" />
              </Flex>
            )
          )}
        </Flex>
      </Flex>
    </Card>
  );
};

export default TeacherReportCard;
